import { ChevronsLeft, Hexagon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { NavLink } from 'react-router-dom';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

import { collectNavItems, type NavItem } from './nav';

const COLLAPSED_KEY = 'cluster_workspace_sidebar_collapsed';

function NavList({
  items,
  collapsed,
  onNavigate,
}: {
  items: NavItem[];
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <nav aria-label="Primary" className="flex flex-col gap-1 px-2">
      <TooltipProvider delayDuration={0}>
        {items.map((item) => {
          const link = (
            <NavLink
              to={item.to}
              end={item.match === 'exact'}
              onClick={onNavigate}
              aria-label={collapsed ? item.label : undefined}
              className={({ isActive }) =>
                cn(
                  'flex h-9 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors motion-reduce:transition-none',
                  isActive
                    ? 'bg-accent text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
                  collapsed && 'justify-center gap-0 px-0',
                )
              }
            >
              <item.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );

          if (!collapsed) {
            return <div key={item.to}>{link}</div>;
          }
          return (
            <Tooltip key={item.to}>
              <TooltipTrigger asChild>{link}</TooltipTrigger>
              <TooltipContent side="right">{item.label}</TooltipContent>
            </Tooltip>
          );
        })}
      </TooltipProvider>
    </nav>
  );
}

/**
 * Sidebar body — shared by the desktop rail and the mobile Sheet (design §6),
 * so the two can never drift apart.
 */
export function SidebarContent({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const items = useMemo(() => collectNavItems(), []);

  return (
    <>
      <div
        className={cn(
          'flex h-16 items-center gap-2 border-b px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        <Hexagon className="h-5 w-5 shrink-0" aria-hidden="true" />
        {!collapsed && <span className="text-sm font-semibold tracking-tight">Workspace</span>}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto py-4">
        <NavList items={items} collapsed={collapsed} onNavigate={onNavigate} />
      </div>
    </>
  );
}

export function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSED_KEY) === 'true');

  useEffect(() => {
    localStorage.setItem(COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  return (
    <aside
      className={cn(
        'hidden shrink-0 flex-col border-r bg-card transition-all duration-300 motion-reduce:transition-none lg:flex',
        collapsed ? 'w-16' : 'w-64',
      )}
    >
      <SidebarContent collapsed={collapsed} />
      <div className="border-t p-2">
        <Button
          variant="ghost"
          size="icon"
          className={cn('w-full', !collapsed && 'justify-start gap-3 px-3')}
          onClick={() => setCollapsed((value) => !value)}
          aria-label="Toggle navigation"
        >
          <ChevronsLeft
            className={cn(
              'h-4 w-4 transition-transform motion-reduce:transition-none',
              collapsed && 'rotate-180',
            )}
            aria-hidden="true"
          />
          {!collapsed && <span className="text-sm">Collapse</span>}
        </Button>
      </div>
    </aside>
  );
}
